import * as CryptoJS from 'crypto-js';

export class EncryptionHelper {
  private static keyPromise: Promise<CryptoKey> | null = null;
  private static readonly DB_NAME = 'AppCryptoDB';
  private static readonly STORE_NAME = 'keys';
  private static readonly KEY_ID = 'app_master_key';

  private static hasSubtleCrypto(): boolean {
    return (
      typeof window !== 'undefined' &&
      !!(window.crypto && window.crypto.subtle)
    );
  }

  private static getCrypto(): Crypto {
    if (typeof window !== 'undefined' && window.crypto) {
      return window.crypto;
    }
    throw new Error('Web Crypto API is not available in this environment.');
  }

  private static openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) {
        reject(new Error('IndexedDB is not available in this environment.'));
        return;
      }
      const request = window.indexedDB.open(EncryptionHelper.DB_NAME, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(EncryptionHelper.STORE_NAME)) {
          db.createObjectStore(EncryptionHelper.STORE_NAME);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private static async loadKeyFromIndexedDB(): Promise<CryptoKey | null> {
    try {
      const db = await EncryptionHelper.openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(EncryptionHelper.STORE_NAME, 'readonly');
        const store = tx.objectStore(EncryptionHelper.STORE_NAME);
        const req = store.get(EncryptionHelper.KEY_ID);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  private static async saveKeyToIndexedDB(key: CryptoKey): Promise<void> {
    try {
      const db = await EncryptionHelper.openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(EncryptionHelper.STORE_NAME, 'readwrite');
        const store = tx.objectStore(EncryptionHelper.STORE_NAME);
        const req = store.put(key, EncryptionHelper.KEY_ID);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (e) {
      console.error('Failed to save CryptoKey to IndexedDB:', e);
    }
  }

  private static async getOrGenerateKey(): Promise<CryptoKey> {
    const existingKey = await EncryptionHelper.loadKeyFromIndexedDB();
    if (existingKey) {
      return existingKey;
    }

    const cryptoObj = EncryptionHelper.getCrypto();
    const newKey = await cryptoObj.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // non-extractable: JS cannot export or read raw key bytes
      ['encrypt', 'decrypt']
    );

    await EncryptionHelper.saveKeyToIndexedDB(newKey);
    return newKey;
  }

  private static getKey(): Promise<CryptoKey> {
    if (!EncryptionHelper.keyPromise) {
      EncryptionHelper.keyPromise = EncryptionHelper.getOrGenerateKey();
    }
    return EncryptionHelper.keyPromise;
  }

  static async hash(data: any): Promise<string> {
    if (!data) return '';
    const str = typeof data === 'string' ? data : JSON.stringify(data);

    if (EncryptionHelper.hasSubtleCrypto()) {
      try {
        const msgUint8 = new TextEncoder().encode(str);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      } catch {
        // Fall back to CryptoJS if Web Crypto fails
      }
    }

    return CryptoJS.SHA256(str).toString(CryptoJS.enc.Hex);
  }

  static async encrypt(data: string): Promise<string> {
    if (!data) return '';

    if (EncryptionHelper.hasSubtleCrypto()) {
      try {
        const cryptoObj = EncryptionHelper.getCrypto();
        const key = await EncryptionHelper.getKey();

        const iv = cryptoObj.getRandomValues(new Uint8Array(12)); // 96-bit IV for AES-GCM
        const encodedData = new TextEncoder().encode(data);

        const ciphertext = await cryptoObj.subtle.encrypt(
          { name: 'AES-GCM', iv },
          key,
          encodedData
        );

        const combined = new Uint8Array(iv.length + ciphertext.byteLength);
        combined.set(iv, 0);
        combined.set(new Uint8Array(ciphertext), iv.length);

        let binary = '';
        const len = combined.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(combined[i]);
        }
        return window.btoa(binary);
      } catch (e) {
        console.error('Web Crypto encryption failed, falling back to CryptoJS:', e);
      }
    }

    return CryptoJS.AES.encrypt(data, EncryptionHelper.KEY_ID).toString();
  }

  static async decrypt(data: string): Promise<string | null> {
    if (!data) return null;

    if (EncryptionHelper.hasSubtleCrypto()) {
      try {
        const cryptoObj = EncryptionHelper.getCrypto();
        const key = await EncryptionHelper.getKey();

        const binary = window.atob(data);
        const combined = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          combined[i] = binary.charCodeAt(i);
        }

        if (combined.length >= 13) {
          const iv = combined.slice(0, 12);
          const ciphertext = combined.slice(12);

          const decryptedBuffer = await cryptoObj.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            ciphertext
          );

          return new TextDecoder().decode(decryptedBuffer);
        }
      } catch {
        // Fall back to CryptoJS if Web Crypto decryption fails (e.g. CryptoJS-encrypted payload)
      }
    }

    try {
      const bytes = CryptoJS.AES.decrypt(data, EncryptionHelper.KEY_ID);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || null;
    } catch {
      return null;
    }
  }

  static getUrlCodedString(data: string): string {
    return window.encodeURIComponent(data);
  }
}
